/**
 * backendClient.js — all Email Insights backend API calls.
 * Uses absolute backend URL to avoid React dev-server proxy issues.
 */

function getBackendBase() {
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:3001';
  }
  return window.location.origin;
}

const BASE = () => `${getBackendBase()}/api/v1/email-insights`;

/**
 * Base for the Gmail proxy routes. gmailClient builds its own URLs against this
 * because it drives its own retry/backoff loop and needs the raw Response.
 */
export const gmailProxyBase = () => `${BASE()}/gmail`;

/** Authorization header for the proxy — the TeamGrid JWT, never a Google token. */
export function getAuthHeaders() {
  return getHeaders();
}

function getHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function request(method, path, body) {
  const res = await fetch(`${BASE()}${path}`, {
    method,
    headers: getHeaders(),
    cache: 'no-store',
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `Request failed: ${res.status}`);
    err.tokenExpired = data.tokenExpired || false;
    err.status = res.status;
    throw err;
  }
  return data;
}

// ── Token endpoints ────────────────────────────────────────────────────────

/**
 * Exchange Google authorization code for tokens on the backend.
 * Backend uses client secret to exchange the code, obtaining both
 * the access token and refresh token, then stores them encrypted.
 * Returns { accessToken, connectedAt, expiresAt }
 */
export async function saveGmailToken(code, oauthClientKey = null) {
  const body = { code };
  if (oauthClientKey) body.oauthClientKey = oauthClientKey;
  return request('POST', '/token/exchange', body);
}

/**
 * Proactively refresh the access token using the stored refresh token.
 * Returns { token, expiresAt } or throws with needsReconnect=true if refresh token is invalid.
 */
export async function refreshGmailToken() {
  return request('POST', '/token/refresh');
}

/** Get connection status — { connected, connectedAt?, expiresAt? } */
export async function getTokenStatus() {
  return request('GET', '/token/status');
}

/**
 * ADDITIVE: which Google OAuth clientId this user should use to connect.
 * Non-Ergode → default. Ergode → fill-order ergode-1/2/3.
 * Returns { clientId, redirectUri, oauthClientKey }
 */
export async function getOAuthConfig() {
  return request('GET', '/token/oauth-config');
}

/** Ergode: Google blocked this OAuth slot — close it for new connects. */
export async function reportOAuthBlock(oauthClientKey, reason) {
  return request('POST', '/token/report-oauth-block', { oauthClientKey, reason });
}

/** Disconnect Gmail — revokes at Google, then deletes token + cache on backend */
export async function disconnectGmail() {
  return request('DELETE', '/token/disconnect');
}

// ── Team endpoints ─────────────────────────────────────────────────────────

/** Get reportees or org-wide members with gmailConnected status
 *  scope: 'reportees' (default) | 'org' (org_admin only)
 *  search: optional name/email filter
 */
export async function getTeam(page = 1, limit = 12, scope = 'reportees', search = '') {
  const q = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    scope: scope === 'org' ? 'org' : 'reportees',
  });
  const s = (search || '').trim();
  if (s) q.set('search', s);
  return request('GET', `/team?${q.toString()}`);
}

/** One member's Gmail token — used by the Team Apps screen (TeamAppsV2). */
export async function getMemberToken(memberId) {
  return request('GET', `/team/${memberId}/token`);
}

/** Check cache for reportee metrics */
export async function getMemberCache(memberId, fromDate, toDate) {
  return request('GET', `/team/${memberId}/cache?fromDate=${fromDate}&toDate=${toDate}`);
}

/** Save computed metrics to reportee cache */
export async function saveMemberCache(memberId, fromDate, toDate, metrics) {
  return request('POST', `/team/${memberId}/cache`, { fromDate, toDate, metrics });
}

// ── Admin endpoints ────────────────────────────────────────────────────────

/**
 * Admin-only: returns Gmail connection status for every active member in the
 * caller's organization.
 * Response shape: { total, connectedCount, notConnectedCount, members: [...] }
 */
export async function getOrgGmailStatus() {
  return request('GET', '/admin/org-gmail-status');
}
